/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { Box, FormControlLabel, Radio, Checkbox, Typography } from '@mui/material';
import { IFormValues, PatternType, PatternTypes, quarterOptions } from '../../AllSchedules.Model';
import { PatternFields } from '../PatternFields/PatternFields';
import { Icons } from '../../../../../../Shared/Constants/Icons';
import { JSX } from 'react/jsx-runtime';
import { UseFormSetValue } from 'react-hook-form';

interface IQuarterlyScheduleProps {
  t: (key: string) => string;
  patternType: PatternType;
  setValue: UseFormSetValue<IFormValues>;
  getValues: (field: string) => any;
  applyToAllQuarters: boolean;
  setApplyToAllQuarters: (checked: boolean) => void;
  previousQuartersDataRef: React.MutableRefObject<any>;
  isLoading: boolean;
  control: any;
  updateAllQuarters: (fieldType: string, value: string) => void;
  isMonthDropdownOpen: boolean;
  setIsMonthDropdownOpen: (open: boolean) => void;
  isWeekDropdownOpen: boolean;
  setIsWeekDropdownOpen: (open: boolean) => void;
  isDayDropdownOpen: boolean;
  setIsDayDropdownOpen: (open: boolean) => void;
  dropdownOpenStates: Record<string, boolean>;
  setDropdownOpenStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  getStateKey: (quarter: string, field: string) => string;
  radioButton: any;
  radioButtonText: any;
  textStyle: any;
  quarterBoxStyle: any;
}
type QuarterKey = `quarters.${'Q1' | 'Q2' | 'Q3' | 'Q4'}`;

const QuarterlySchedule: React.FC<IQuarterlyScheduleProps> = ({
  t,
  patternType,
  setValue,
  getValues,
  applyToAllQuarters,
  setApplyToAllQuarters,
  previousQuartersDataRef,
  isLoading,
  control,
  updateAllQuarters,
  isMonthDropdownOpen,
  setIsMonthDropdownOpen,
  isWeekDropdownOpen,
  setIsWeekDropdownOpen,
  isDayDropdownOpen,
  setIsDayDropdownOpen,
  dropdownOpenStates,
  setDropdownOpenStates,
  getStateKey,
  radioButton,
  radioButtonText,
  textStyle,
  quarterBoxStyle,
}) => {
  const CustomDropdownIcon = ({ open, ...props }: { open?: boolean; [key: string]: any }) => {
    return (
      <img
        {...props}
        src={Icons.DropdownUpIcon}
        style={{
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.3s',
          cursor: 'pointer',
          position: 'absolute',
          pointerEvents: 'none',
        }}
      />
    );
  };
  return (
    <>
      <Box sx={quarterBoxStyle}>
        {Object.values(PatternTypes).map((type) => (
          <FormControlLabel
            key={type}
            control={
              <Radio checked={patternType === type} onChange={() => setValue('patternType', type)} sx={radioButton} />
            }
            label={type}
            sx={radioButtonText}
          />
        ))}
      </Box>

      <Box sx={{ marginBottom: '2rem' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={applyToAllQuarters}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  // Capture current values of all quarters with proper typing
                  const currentQuartersData: Partial<IFormValues['quarters']> = {};
                  quarterOptions.forEach((q) => {
                    const field = `quarters.${q}` as QuarterKey;
                    currentQuartersData[q as keyof typeof currentQuartersData] = getValues(field);
                  });
                  previousQuartersDataRef.current = currentQuartersData;
                  // Apply Q1's values to all quarters
                  const q1Values = getValues('quarters.Q1');
                  quarterOptions.forEach((q) => {
                    const field = `quarters.${q}` as QuarterKey;
                    setValue(field, q1Values);
                  });
                } else {
                  // Restore previous values for each quarter
                  quarterOptions.forEach((q) => {
                    const field = `quarters.${q}` as QuarterKey;
                    setValue(field, previousQuartersDataRef.current[q]);
                  });
                }
                setApplyToAllQuarters(checked);
              }}
              icon={<img src={Icons.Unchecked} alt="unchecked" />}
              checkedIcon={
                <img
                  src={Icons.CheckedIcon}
                  alt="checked"
                  style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                />
              }
              disabled={isLoading}
            />
          }
          label={t('jobManagement:applySameChangesToAll')}
          sx={textStyle}
        />
      </Box>

      {applyToAllQuarters ? (
        <Box sx={{ mb: 2, display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Typography>All Quarters</Typography>
          <PatternFields
            patternType={patternType}
            control={control}
            quarterName="Q1"
            onFieldChange={updateAllQuarters}
            selectProps={{
              month: {
                IconComponent: (props: JSX.IntrinsicAttributes & { [key: string]: any; open?: boolean }) =>
                  CustomDropdownIcon({ ...props, open: isMonthDropdownOpen }),
                open: isMonthDropdownOpen,
                onOpen: () => setIsMonthDropdownOpen(true),
                onClose: () => setIsMonthDropdownOpen(false),
              },
              week: {
                IconComponent: (props: JSX.IntrinsicAttributes & { [key: string]: any; open?: boolean }) => (
                  <CustomDropdownIcon {...props} open={isWeekDropdownOpen} />
                ),
                open: isWeekDropdownOpen,
                onOpen: () => setIsWeekDropdownOpen(true),
                onClose: () => setIsWeekDropdownOpen(false),
              },
              day: {
                IconComponent: (props: JSX.IntrinsicAttributes & { [key: string]: any; open?: boolean }) => (
                  <CustomDropdownIcon {...props} open={isDayDropdownOpen} />
                ),
                open: isDayDropdownOpen,
                onOpen: () => setIsDayDropdownOpen(true),
                onClose: () => setIsDayDropdownOpen(false),
              },
            }}
          />
        </Box>
      ) : (
        quarterOptions.map((quarter) => (
          <Box key={quarter} sx={{ display: 'flex', flexDirection: 'row', gap: '1rem', marginBottom: '1rem' }}>
            <Typography>{quarter}</Typography>
            <PatternFields
              patternType={patternType}
              control={control}
              quarterName={quarter}
              selectProps={{
                month: {
                  IconComponent: (props: JSX.IntrinsicAttributes & { [key: string]: any; open?: boolean }) => (
                    <CustomDropdownIcon {...props} open={dropdownOpenStates[getStateKey(quarter, 'month')] || false} />
                  ),
                  open: dropdownOpenStates[getStateKey(quarter, 'month')] || false,
                  onOpen: () => setDropdownOpenStates((prev) => ({ ...prev, [getStateKey(quarter, 'month')]: true })),
                  onClose: () => setDropdownOpenStates((prev) => ({ ...prev, [getStateKey(quarter, 'month')]: false })),
                },
                week: {
                  IconComponent: (props: JSX.IntrinsicAttributes & { [key: string]: any; open?: boolean }) => (
                    <CustomDropdownIcon {...props} open={dropdownOpenStates[getStateKey(quarter, 'week')] || false} />
                  ),
                  open: dropdownOpenStates[getStateKey(quarter, 'week')] || false,
                  onOpen: () => setDropdownOpenStates((prev) => ({ ...prev, [getStateKey(quarter, 'week')]: true })),
                  onClose: () => setDropdownOpenStates((prev) => ({ ...prev, [getStateKey(quarter, 'week')]: false })),
                },
                day: {
                  IconComponent: (props: JSX.IntrinsicAttributes & { [key: string]: any; open?: boolean }) => (
                    <CustomDropdownIcon {...props} open={dropdownOpenStates[getStateKey(quarter, 'day')] || false} />
                  ),
                  open: dropdownOpenStates[getStateKey(quarter, 'day')] || false,
                  onOpen: () => setDropdownOpenStates((prev) => ({ ...prev, [getStateKey(quarter, 'day')]: true })),
                  onClose: () => setDropdownOpenStates((prev) => ({ ...prev, [getStateKey(quarter, 'day')]: false })),
                },
              }}
            />
          </Box>
        ))
      )}
    </>
  );
};

export default QuarterlySchedule;
